import { useMemo, useEffect, useRef } from 'react'
import {
  Button,
  CopyButton,
  Body,
  Link,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from '@panwds/react-ui'
import { _T } from '../../i18n'
import { getDatadogRum } from '../../framework/externals'
import { getCurrentTsgDebugInfo } from '../../framework/debugInfo'
import { getFrameworkVar } from '../../framework/states'
import { __isDebugOn } from '../../init/debug'
import { useCurrentLocationUrl } from './navUtils'

import type { VFC, ReactNode } from 'react'
import type { RenderModalProps } from '@panwds/react-ui'

const BUTTON_COPY = _T('Copy')
const BUTTON_CLOSE = _T('Close')
const LABEL_PRODUCT_INFO = 'Debug Information'

export const DebugInfoModal: VFC<RenderModalProps> = ({
  closeModal,
  isOpen,
}) => {
  const curUrl = useCurrentLocationUrl()
  const datadogRum = getDatadogRum()
  const [title, table, copyText, copyButton] = useMemo(() => {
    const info = getCurrentTsgDebugInfo(curUrl)
    const urlLinkComp = <Link
      href={info.url}
      rel='noopener'
      target='_blank'
      className='twsf-cursor-default hover:twsf-cursor-default twsf-no-underline hover:twsf-select-all'
      onClick={(e) => {
        e.preventDefault()
      }}
    >{info.url}</Link>
    const urlLink = Object.defineProperty(Object.create(urlLinkComp), 'toString', { value: () => info.url })
    const cspAccount = info.support_account_name ? `${info.support_account_name} - ${info.support_account_id}` : `${info.support_account_id || ''} (no access)`
    const isProd = getFrameworkVar('production') && !__isDebugOn()
    const getPage = () => {
      if (!info.page_name) {
        return
      }
      if (info.page_micro_app && !isProd) {
        const comp = <>{info.page_name} <small>(from micro-app: {info.page_micro_app})</small></>
        return Object.defineProperty(Object.create(comp), 'toString', {
          value: () => (
            `${info.page_name} from micro-app: ${info.page_micro_app}`
          )
        })
      }
      return info.page_name
    }
    const fields = [
      ['URL', urlLink],
      ['Title', document.title],
      ['Page', getPage()],
      ['TSG ID', info.tsg_id],
      ['TSG Name', info.tsg_name],
      ['CSP Account', cspAccount],
      ['USERID', info.userid],
      ['NSCSID', info.netsec_session_id],
      ['SESSID', info.dd_session_id],
      ['VIEWID', info.dd_view_id],
    ].filter(field => field[1]) as [string, ReactNode][]
    const copyText = [
      ...fields,
      ['DETAILS', JSON.stringify(info, null, 2)]
    ].map(([name, value]) => `${name}: ${value}`).join('\n')
    const table = <table className='twsf-w-full twsf-mt-2 twsf-mb-4 twsf-text-sm' style={{ tableLayout: 'fixed' }}>
      <tbody>
        {fields.map(([name, value]) => (
          <tr key={name}>
            <td className='twsf-align-top'><strong>{name}</strong></td>
            <td className='twsf-align-top twsf-break-all' style={{ width: '75%' }}>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
    const copyButton = <CopyButton
      text={copyText}
      appearance='secondary'
    >{BUTTON_COPY}</CopyButton>
    return [document.title, table, copyText, copyButton]
  }, [curUrl])

  const copyRef = useRef(copyText)
  copyRef.current = copyText

  useEffect(() => {
    const copyListener = (e: ClipboardEvent) => {
      const selection = document.getSelection()?.toString()
      const override = !selection?.length || (
        selection.length > 10 && selection.includes('URL') && selection.includes(datadogRum ? 'VIEWID' : 'USERID')
      )
      if (copyRef.current && override) {
        e.preventDefault()
        e.clipboardData?.setData('text/plain', copyRef.current)
      }
    }
    document.addEventListener('copy', copyListener)
    return () => {
      document.removeEventListener('copy', copyListener)
    }
  }, [datadogRum])

  const btnRef = useRef<HTMLButtonElement>(null)
  const help = <Body addClassName='twsf-mt-2 twsf-mb-2'>
    If you’ve filed a support case for this product, please copy and attach to the <br /><a
      href='https://supportcases.paloaltonetworks.com/'
      target='_blank'
      rel='noopener noreferrer'
    >Support Case</a> to facilitate resolution.
  </Body>
  const closeButton = useMemo(() => (
    <Button onClick={closeModal} appearance='primary' ref={btnRef}>
      {BUTTON_CLOSE}
    </Button>
  ), [closeModal])

  useEffect(() => {
    if (btnRef.current) {
      const mask = btnRef.current.closest('[data-testid="modal"]')?.querySelector('.tw-opacity-80, .twds-opacity-80') as HTMLDivElement | undefined
      if (mask) {
        mask.style.display = 'none'
      }
    }
  }, [])

  return (
    <Modal onClose={closeModal} isOpen={isOpen} size='md' eventsToClose={{ enableEscapeKey: true }}>
      <ModalHeader
        title={<>{LABEL_PRODUCT_INFO} - {title}</>}
      />
      <ModalBody addClassName='twsf-pb-0'>
        {table}
        {help}
      </ModalBody>
      <ModalFooter
        addClassName='twsf-justify-between'
        footerMessage={copyButton}
        secondaryContent={closeButton}
      />
    </Modal>
  )
}

export const useDebugInfoModule = (enable?: boolean) => {
  const { openModal, closeModal } = useModal(DebugInfoModal)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (enable && event.ctrlKey && event.shiftKey && (event.altKey || event.metaKey) && event.code === 'KeyX') {
        openModal()
      }
    }

    if (enable) {
      document.addEventListener('keydown', handleKeyDown, { passive: true })
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      closeModal()
    }
  }, [enable, closeModal, openModal])
}
